import * as React from "react";
import {
    Checkbox,
    Chip,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import {useTranslation} from "react-i18next";
import {attr, Branch, formatUtcDateTime, getStatusColor, Order, SortableKey, SortCell} from "../../../../hooks/useBranches";

export interface BranchListingTablesProps {
    loading: boolean;
    sortedBranches: Branch[];
    paginatedBranches: Branch[];
    searchQuery: string;
    order: Order;
    orderBy: SortableKey;
    handleRequestSort: (field: SortableKey) => void;
    selected: string[];
    filteredBranches: Branch[];
    handleSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleRowClick: (id: string) => void;
    isSelected: (id: string) => boolean;
    anchorEl: HTMLElement | null;
    menuOpen: boolean;
    menuRowId: string | null;
    handleMenuOpen: (event: React.MouseEvent<HTMLElement>, id: string) => void;
    handleMenuClose: () => void;
    handleMenuAction: (action: () => void) => void;
    handleStatusUpdate: (ids: string[], status: string) => void;
    handleEditOpen: (row: Branch) => void;
}

export default function BranchListingTables({
                                                loading,
                                                sortedBranches,
                                                paginatedBranches,
                                                searchQuery,
                                                order,
                                                orderBy,
                                                handleRequestSort,
                                                selected,
                                                filteredBranches,
                                                handleSelectAll,
                                                handleRowClick,
                                                isSelected,
                                                anchorEl,
                                                menuOpen,
                                                menuRowId,
                                                handleMenuOpen,
                                                handleMenuClose,
                                                handleMenuAction,
                                                handleStatusUpdate,
                                                handleEditOpen,
                                            }: Readonly<BranchListingTablesProps>): React.ReactElement {
    const {t} = useTranslation();
    return (
        <TableContainer component={Paper} sx={{overflowX: "auto"}}>
            <Table sx={{minWidth: {xs: 600, md: 900}}} aria-label="branches table">
                <TableHead>
                    <TableRow>
                        <TableCell padding="checkbox">
                            <Checkbox
                                color="primary"
                                indeterminate={selected.length > 0 && selected.length < filteredBranches.length}
                                checked={filteredBranches.length > 0 && selected.length === filteredBranches.length}
                                onChange={handleSelectAll}
                                inputProps={{"aria-label": "select all branches"}}
                            />
                        </TableCell>
                        <SortCell label={t('hr.branches.table.header.code')} field="code" orderBy={orderBy} order={order}
                                  onSort={handleRequestSort}/>
                        <SortCell label={t('hr.branches.table.header.name')} field="name" orderBy={orderBy} order={order}
                                  onSort={handleRequestSort}/>
                        <SortCell label={t('hr.branches.table.header.city')} field="city" orderBy={orderBy} order={order}
                                  onSort={handleRequestSort}/>
                        <SortCell label={t('hr.branches.table.header.region')} field="region" orderBy={orderBy} order={order}
                                  onSort={handleRequestSort}/>
                        <TableCell>{t('hr.branches.table.header.isActive')}</TableCell>
                        <SortCell label={t('hr.branches.table.header.status')} field="status" orderBy={orderBy} order={order}
                                  onSort={handleRequestSort}/>
                        <SortCell label={t('hr.branches.table.header.updatedAt')} field="updatedAt" orderBy={orderBy} order={order}
                                  onSort={handleRequestSort}/>
                        <TableCell align="center">{t('hr.branches.table.header.actions')}</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {loading ? (
                        Array.from({length: 5}).map((_, i) => (
                            <TableRow key={`skeleton-branch-${i}`}>
                                <TableCell padding="checkbox"><Skeleton variant="rectangular" width={20}
                                                                        height={20}/></TableCell>
                                <TableCell><Skeleton variant="text"/></TableCell>
                                <TableCell><Skeleton variant="text"/></TableCell>
                                <TableCell><Skeleton variant="text"/></TableCell>
                                <TableCell><Skeleton variant="text"/></TableCell>
                                <TableCell><Skeleton variant="text" width={50}/></TableCell>
                                <TableCell><Skeleton variant="text" width={70}/></TableCell>
                                <TableCell><Skeleton variant="text"/></TableCell>
                                <TableCell align="center"><Skeleton variant="circular" width={24}
                                                                    height={24}/></TableCell>
                            </TableRow>
                        ))
                    ) : sortedBranches.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={9} align="center" sx={{py: 6}}>
                                <AccountTreeOutlinedIcon
                                    sx={{fontSize: 48, color: "text.disabled", mb: 1}}/>
                                <Typography variant="body1" color="text.secondary">{t('hr.branches.empty.title')}</Typography>
                                <Typography variant="body2" color="text.disabled">
                                    {searchQuery ? t('hr.branches.empty.adjustSearch') : t('hr.branches.empty.createFirst')}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : (
                        paginatedBranches.map((row) => {
                            const isItemSelected = isSelected(row.id);
                            const labelId = `branch-checkbox-${row.id}`;
                            return (
                                <TableRow
                                    hover
                                    key={row.id}
                                    role="checkbox"
                                    aria-checked={isItemSelected}
                                    tabIndex={-1}
                                    selected={isItemSelected}
                                    onClick={() => handleRowClick(row.id)}
                                    sx={{cursor: "pointer"}}
                                >
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            color="primary"
                                            checked={isItemSelected}
                                            inputProps={{"aria-labelledby": labelId}}
                                        />
                                    </TableCell>
                                    <TableCell component="th" id={labelId}
                                               scope="row">{attr(row, "code", row.code)}</TableCell>
                                    <TableCell>{row.name ?? "-"}</TableCell>
                                    <TableCell>{attr(row, "city", row.city)}</TableCell>
                                    <TableCell>{attr(row, "region", row.region)}</TableCell>
                                    <TableCell>
                                        {(() => {
                                            const isActive = attr(row, "is_active", String(row.isActive)).toUpperCase() === "TRUE";
                                            return (
                                                <Chip
                                                    label={isActive ? t('common.actions.yes') : t('common.actions.no')}
                                                    size="small"
                                                    color={isActive ? "success" : "default"}
                                                    variant="outlined"
                                                />
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={attr(row, "status", row.status)}
                                            size="small"
                                            color={getStatusColor(attr(row, "status", row.status))}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>{formatUtcDateTime(attr(row, "updated_at", row.updatedAt))}</TableCell>
                                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                        <IconButton
                                            aria-label="more"
                                            id={`branch-action-btn-${row.id}`}
                                            aria-controls={menuOpen && menuRowId === row.id ? `branch-action-menu-${row.id}` : undefined}
                                            aria-haspopup="true"
                                            aria-expanded={menuOpen && menuRowId === row.id ? "true" : undefined}
                                            onClick={(e) => handleMenuOpen(e, row.id)}
                                        >
                                            <MoreVertIcon/>
                                        </IconButton>
                                        <Menu
                                            id={`branch-action-menu-${row.id}`}
                                            anchorEl={anchorEl}
                                            open={menuOpen && menuRowId === row.id}
                                            onClose={handleMenuClose}
                                            slotProps={{
                                                paper: {
                                                    style: {
                                                        maxHeight: 48 * 4.5,
                                                        width: "20ch"
                                                    }
                                                }
                                            }}
                                        >
                                            <MenuItem
                                                onClick={() => handleMenuAction(() => handleStatusUpdate([row.id], "APPROVED"))}>
                                                <ListItemIcon><CheckCircleOutlineIcon fontSize="small"
                                                                                      color="success"/></ListItemIcon>
                                                <ListItemText>{t('common.actions.approve')}</ListItemText>
                                            </MenuItem>
                                            <MenuItem
                                                onClick={() => handleMenuAction(() => handleEditOpen(row))}>
                                                <ListItemIcon><EditIcon fontSize="small"
                                                                        color="primary"/></ListItemIcon>
                                                <ListItemText>{t('common.actions.edit')}</ListItemText>
                                            </MenuItem>
                                            <Divider/>
                                            <MenuItem
                                                onClick={() => handleMenuAction(() => handleStatusUpdate([row.id], "REJECTED"))}>
                                                <ListItemIcon><CancelOutlinedIcon fontSize="small"
                                                                                  color="error"/></ListItemIcon>
                                                <ListItemText>{t('common.actions.reject')}</ListItemText>
                                            </MenuItem>
                                        </Menu>
                                    </TableCell>
                                </TableRow>
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
